'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Plane } from 'lucide-react';

export default function AirportSearchInput({ label, value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const inputId = `airport-search-${(label || 'field').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  // Sync if parent changes value
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchCities = useCallback((q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/cities?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setResults(data);
        setOpen(data.length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 220);
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    fetchCities(val);
  }

  function select(city) {
    const formatted = `${city.city} (${city.iata})`;
    setQuery(formatted);
    onChange(formatted);
    setResults([]);
    setOpen(false);
  }

  return (
    <div ref={wrapRef} className="relative">
      <label htmlFor={inputId} className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">
        {label}
      </label>
      <div className="relative">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-3.5 h-3.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        <input
          id={inputId}
          className="w-full pl-9 pr-8 py-3 rounded-xl border border-slate-200 text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm"
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={() => { if (query.length >= 2) fetchCities(query); }}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 w-[300px] top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden max-h-72 overflow-y-auto">
          {results.map((c, i) => (
            <button
              key={`${c.iata}-${i}`}
              type="button"
              onMouseDown={e => { e.preventDefault(); select(c); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-sky-50 transition-colors ${i > 0 ? 'border-t border-slate-100' : ''}`}
            >
              <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-sky-600 font-bold text-sm">{c.iata}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{c.city}</p>
                <p className="text-xs text-slate-400 truncate">
                  {c.name}{c.country ? ` · ${c.country}` : ''}
                  {c.state ? `, ${c.state}` : ''}
                </p>
              </div>
              <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                <Plane size={13} className="text-slate-300" />
                {c.continent && (
                  <span className="text-[10px] text-slate-300">{c.continent}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
